/**
 * Shared EOD Form Layout with James Webster branding
 * Matches the login page design - dark glassmorphic cards
 */

import type { ReactNode } from "react";
import { BrandLogo, companyName } from "../../components/BrandLogo";

interface EODFormLayoutProps {
  title: string;
  subtitle: string;
  children: ReactNode;
}

export function EODFormLayout({ title, subtitle, children }: EODFormLayoutProps) {
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      {/* Background decoration - matching login page */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-red-600/30 to-red-900/20 rounded-full blur-3xl" />
        <div className="absolute top-1/4 -left-40 w-[600px] h-[600px] bg-gradient-to-tr from-red-800/25 to-orange-600/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 right-1/4 w-[400px] h-[400px] bg-gradient-to-tl from-red-700/20 to-red-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 py-6 px-4 sm:px-8">
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-3">
          <BrandLogo variant="primary" className="h-10 w-auto" />
          <span className="text-xl font-bold text-white">{companyName}</span>
        </div>
      </header>

      {/* Form Container */}
      <main className="relative z-10 flex-1 py-6 px-4 sm:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Title Section */}
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">{title}</h1>
            <p className="text-white/60">{subtitle}</p>
          </div>

          {/* Form Card - Dark glassmorphic like login */}
          <div className="bg-[#141414]/90 backdrop-blur-xl rounded-2xl border border-white/10 p-6 sm:p-8 shadow-2xl">
            {children}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-6 px-4">
        <p className="text-center text-xs text-white/40">
          © {new Date().getFullYear()} {companyName}. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

// Shared form field components
export function FormField({
  label,
  required,
  children,
  hint,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-white/90">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-white/50">{hint}</p>}
    </div>
  );
}

export function TextInput({
  type = "text",
  value,
  onChange,
  placeholder,
  required,
  min,
  max,
  step,
}: {
  type?: "text" | "number" | "date";
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      min={min}
      max={max}
      step={step}
      className="w-full px-4 py-3 rounded-xl bg-[#1f1f1f] border-0 text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-red-500/30 transition-all [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-50"
    />
  );
}

export function TextArea({
  value,
  onChange,
  placeholder,
  required,
  rows = 3,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      rows={rows}
      className="w-full px-4 py-3 rounded-xl bg-[#1f1f1f] border-0 text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-red-500/30 transition-all resize-none"
    />
  );
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
  required,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full px-4 py-3 rounded-xl bg-[#1f1f1f] border-0 text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 transition-all appearance-none cursor-pointer"
      style={{ colorScheme: "dark" }}
    >
      {placeholder && (
        <option value="" disabled className="text-white/40">
          {placeholder}
        </option>
      )}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value} className="bg-[#1f1f1f] text-white">
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export function RatingInput({
  value,
  onChange,
  max = 10,
}: {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  max?: number;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {Array.from({ length: max + 1 }, (_, i) => i).map((num) => (
        <button
          key={num}
          type="button"
          onClick={() => onChange(value === num ? undefined : num)}
          className={`w-10 h-10 rounded-xl text-sm font-medium transition-all ${
            value === num
              ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/25"
              : "bg-[#1f1f1f] text-white/60 hover:bg-[#2a2a2a] hover:text-white/80"
          }`}
        >
          {num}
        </button>
      ))}
    </div>
  );
}

export function SubmitButton({
  loading,
  disabled,
}: {
  loading: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="submit"
      disabled={loading || disabled}
      className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-base hover:shadow-lg hover:shadow-red-500/25 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 transition-all duration-300 flex items-center justify-center gap-2"
    >
      {loading ? (
        <>
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span>Submitting...</span>
        </>
      ) : (
        <span>Submit EOD Report</span>
      )}
    </button>
  );
}

export function SuccessMessage({ onReset }: { onReset: () => void }) {
  return (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
        <svg className="w-10 h-10 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">EOD Submitted!</h2>
      <p className="text-white/60 mb-8">Your end of day report has been recorded.</p>
      <button
        onClick={onReset}
        className="px-6 py-3 rounded-xl bg-[#1f1f1f] text-white font-medium hover:bg-[#2a2a2a] transition-all"
      >
        Submit Another Report
      </button>
    </div>
  );
}

export function ErrorMessage({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 mb-6">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="flex-1">
          <p className="text-red-400 text-sm">{message}</p>
          <button onClick={onRetry} className="text-red-400 text-sm underline mt-1 hover:text-red-300">
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}
